import {
  useHttpClient,
  useNetworkStatus,
  useReducerStorage as useReducer,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { OrganizationBusinessUnit } from './organizationBusinessUnits.models';
import {
  initState,
  organizationBusinessUnitsReducer,
  OrganizationBusinessUnitsState,
} from './organizationBusinessUnits.reducer';

interface OrganizationBusinessUnitsContextProps {
  businessUnits: OrganizationBusinessUnit[];
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
  fetchOrganizationBusinessUnits: (organizationId: string) => Promise<unknown>;
}

const OrganizationBusinessUnitsContext = createContext<
  OrganizationBusinessUnitsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<OrganizationBusinessUnitsState>;
}>;

export const OrganizationBusinessUnitsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(
    organizationBusinessUnitsReducer,
    { ...initState, ...initialState },
    {
      key: 'airship-cache:organization-business-units',
      select: (state) => ({ ids: state.ids, entities: state.entities }),
    }
  );
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const businessUnits = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .sort((a, b) => a.name.localeCompare(b.name)),
    [state.entities, state.ids]
  );

  const fetchOrganizationBusinessUnits: OrganizationBusinessUnitsContextProps['fetchOrganizationBusinessUnits'] =
    useCallback(
      async (organizationId) => {
        if (!isOnline()) {
          dispatch({
            type: 'SET_LOADING_STATE',
            payload: { isLoading: false, isLoaded: true },
          });
          return;
        }

        dispatch({ type: 'FETCH_ORGANIZATION_BUSINESS_UNITS' });
        try {
          const businessUnits = await httpClient.get<
            OrganizationBusinessUnit[]
          >(`/organizations/${organizationId}/business-units`);
          dispatch({
            type: 'FETCH_ORGANIZATION_BUSINESS_UNITS_SUCCESS',
            payload: { businessUnits },
          });
        } catch (error) {
          dispatch({
            type: 'FETCH_ORGANIZATION_BUSINESS_UNITS_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient, isOnline]
    );

  return (
    <OrganizationBusinessUnitsContext.Provider
      value={{
        businessUnits,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        error: state.error,
        fetchOrganizationBusinessUnits,
      }}
    >
      {children}
    </OrganizationBusinessUnitsContext.Provider>
  );
};

export const useOrganizationBusinessUnits = () => {
  const context = useContext(OrganizationBusinessUnitsContext);
  if (context === undefined) {
    throw new Error(
      'useOrganizationBusinessUnits must be used within a OrganizationBusinessUnitsProvider'
    );
  }

  return context;
};
